/**
 * Validates text to ensure it doesn't contain HTML tags or unwanted special characters
 * @param text - The text to validate
 * @returns Object with isValid boolean and sanitized text
 */
import { stripHtml } from './sanitize';

export function validateAndSanitizeText(text: string): { isValid: boolean; sanitized: string; error?: string } {
  if (!text || text.trim().length === 0) {
    return { isValid: true, sanitized: text }; // Empty is valid (for optional fields)
  }

  // Check for HTML tags (including script, div, etc.)
  const hasHtmlTags = /<[^>]+>/.test(text);

  if (hasHtmlTags) {
    return {
      isValid: false,
      sanitized: stripHtml(text),
      error: 'HTML tags are not allowed. Please use plain text only.'
    };
  }

  // Check for potentially dangerous special characters
  // Allow: letters, numbers, spaces, common punctuation (.,!?;:), hyphens, underscores, parentheses, apostrophes
  // Block: < > & " ' / \ { } [ ] | ` ~ @ # $ % ^ * + = 
  const dangerousCharsPattern = /[<>"&\/\\{}[\]|`~@#$%^*+=]/g;
  const hasDangerousChars = dangerousCharsPattern.test(text);

  if (hasDangerousChars) {
    return {
      isValid: false,
      sanitized: text.replace(dangerousCharsPattern, ''),
      error: 'Special characters like < > & " / \\ { } [ ] | ` ~ @ # $ % ^ * + = are not allowed.'
    };
  }

  // Text is valid
  return { isValid: true, sanitized: text };
}

/**
 * Validates text on input change - strips HTML tags and dangerous characters in real-time
 * @param text - The text to sanitize
 * @returns Sanitized text
 */
export function sanitizeTextOnInput(text: string): string {
  let sanitized = stripHtml(text);
  
  // Remove dangerous special characters
  sanitized = sanitized.replace(/[<>"&\/\\{}[\]|`~@#$%^*+=]/g, '');
  
  return sanitized;
}

/**
 * Validates text for form submission
 * @param text - The text to validate
 * @param fieldName - Name of the field for error messages
 * @param required - Whether the field is required
 * @returns Object with isValid boolean and error message
 */
export function validateTextForSubmission(
  text: string,
  fieldName: string,
  required: boolean = false
): { isValid: boolean; error?: string } {
  if (required && (!text || text.trim().length === 0)) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (!text || text.trim().length === 0) {
    return { isValid: true }; // Empty is valid for optional fields
  }

  const validation = validateAndSanitizeText(text);
  
  if (!validation.isValid) {
    return { isValid: false, error: validation.error };
  }

  return { isValid: true };
}

